import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';

const ConEditFolder = () => {
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState("");
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState("");

  const project = JSON.parse(localStorage.getItem("selectedProject") || "null");
  const projectId = project ? project.id : null;

  const fetchFolders = async () => {
    try {
      const response = await fetch(`/api/folders?module=construction&projectId=${projectId}`);
      if (!response.ok) throw new Error('Failed to fetch folders');
      const data = await response.json();
      setFolders(data);
    } catch (err) {
      setError('Failed to load folders');
    }
  };

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    if (!role || !projectId) {
      navigate("/");
    } else {
      setUserRole(role);
      fetchFolders();
    }
  }, [navigate, projectId]);

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: newFolderName,
          projectId: projectId,
          module: 'construction'
        })
      });
      if (!response.ok) throw new Error('Failed to create folder');
      setNewFolderName('');
      fetchFolders();
    } catch (err) {
      setError('Failed to create folder');
    }
  };

  const handleDeleteFolder = async (folderId, folderName) => {
    // 显示确认对话框
    if (!window.confirm(`Are you sure you want to delete folder "${folderName}"?`)) {
      return; // 用户取消，中止操作
    }
    
    try {
      const response = await fetch(`/api/folders/${folderId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete folder');
      }
      fetchFolders();
    } catch (err) {
      console.error("Delete folder error:", err);
      setError(err.message || 'Failed to delete folder');
    }
  };

  const handleUpdateFolder = async (folderId) => {
    if (!editingFolderName.trim()) return;
    try {
      const response = await fetch(`/api/folders/${folderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          name: editingFolderName.trim(),
          projectId: projectId,
          module: "construction" 
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update folder");
      }
      setEditingFolderId(null);
      setEditingFolderName("");
      fetchFolders();
    } catch (err) {
      console.error("Update folder error:", err);
      setError(err.message || "Failed to update folder");
    }
  };

  return (
    <div className="flex h-screen">
      <Sidebar userRole={userRole} projectId={projectId} scenario="construction" />
      <div className="flex-1 p-6 bg-gray-50 text-[12px] ml-[60px]">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-[18px]">Manage Construction Folders</h2>
          <img src="/images/gemterra-logo-20260112-t.png" alt="Gemterra" className="h-6" />
        </div>
        <form onSubmit={handleCreateFolder} className="flex gap-4 mb-6">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="New folder name"
            className="border px-3 py-1 rounded w-64 text-[12px]"
          />
          <button
            type="submit"
            className="text-white px-3 py-1 rounded text-[12px]"
            style={{ backgroundColor: "#6FA944" }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#5C8E37")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#6FA944")}
          >
            Create Folder
          </button>
          <button
            type="button"
            onClick={() => navigate("/construction_files_folder")}
            className="bg-gray-300 text-black px-3 py-1 rounded hover:bg-gray-400 text-[12px]"
          >
            Back
          </button>
        </form>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        <div className="w-[420px]">
          <table className="w-full text-left border-t border-gray-300 text-[12px]">
            <thead>
              <tr>
                <th className="py-2 px-2">Folder Name & Actions</th>
              </tr>
            </thead>
            <tbody>
              {folders.map((folder) => (
                <tr key={folder._id} className="border-t border-gray-200 text-[12px]">
                  <td className="py-2 px-2 text-[12px] flex items-center gap-2">
                    {editingFolderId === folder._id ? (
                      <>
                        <input
                          type="text"
                          value={editingFolderName}
                          onChange={(e) => setEditingFolderName(e.target.value)}
                          className="border px-2 py-1 rounded w-64"
                        />
                        <button
                          onClick={() => handleUpdateFolder(folder._id)}
                          className="text-green-600 hover:text-green-800 mr-2"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingFolderId(null)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span>{folder.name}</span>
                        <button
                          onClick={() => {
                            setEditingFolderId(folder._id);
                            setEditingFolderName(folder.name);
                          }}
                          className="text-blue-500 hover:text-blue-700 mr-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteFolder(folder._id, folder.name)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ConEditFolder;